import React, { useState, useEffect } from 'react';
import { X, Calendar, Truck, DollarSign, Clock, Plus, Package, Anchor, AlertTriangle, Edit2, Hash, Layers, FileText, Trash2, ShieldCheck, User } from 'lucide-react';

export default function StopCardModal({ stop, isOpen, onClose, onSaveStop, onDeleteStop, jobs = [], clients = [], rounds = [], stops = [], unassignedStops = [], stopDependencies = [], onReloadData }) {
  if (!isOpen || !stop) return null;

  const [activeTab, setActiveTab] = useState('general');
  const [selectedLinkStopId, setSelectedLinkStopId] = useState('');
  const [selectedPredId, setSelectedPredId] = useState('');
  const [selectedSuccId, setSelectedSuccId] = useState('');
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Initialize time windows list
  const initialTimeWindows = (() => {
    if (Array.isArray(stop.time_windows) && stop.time_windows.length > 0) {
      return stop.time_windows;
    }
    if (stop.time_window_start) {
      return [{ start: stop.time_window_start.slice(0, 5), end: (stop.time_window_end || '18:00').slice(0, 5) }];
    }
    return [{ start: '08:00', end: '18:00' }];
  })();

  const [formData, setFormData] = useState({
    id: stop.id,
    stop_number: stop.stop_number || `STOP-${stop.id}`,
    job_id: stop.job_id || null,
    client_id: stop.client_id || (clients[0]?.id || 1),
    type: stop.type || 'delivery',
    status: stop.status || 'pending',
    weight_kg: stop.weight_kg !== undefined ? stop.weight_kg : 100.0,
    volume_m3: stop.volume_m3 !== undefined ? stop.volume_m3 : 0.5,
    revenue: stop.revenue !== undefined ? stop.revenue : 0.0,
    service_time_mins: stop.service_time_mins !== undefined ? stop.service_time_mins : 10,
    sequence_order: stop.sequence_order !== undefined ? stop.sequence_order : 1,
    estimated_index: stop.estimated_index !== undefined ? stop.estimated_index : 1,
    real_index: stop.real_index !== undefined && stop.real_index !== null ? stop.real_index : '',
    estimated_arrival: stop.estimated_arrival || '08:00',
    actual_arrival: stop.actual_arrival ? String(stop.actual_arrival).slice(11, 16) : '',
    delivery_date: stop.delivery_date ? String(stop.delivery_date).slice(0, 10) : '2026-07-29',
    default_round_id: stop.default_round_id || '',
    instructions: stop.instructions || '',
    driver_notes: stop.driver_notes || '',
    time_windows: initialTimeWindows,
    related_pickup_stop_id: stop.related_pickup_stop_id || null,
    related_delivery_stop_id: stop.related_delivery_stop_id || null
  });

  useEffect(() => {
    if (stop) {
      const tws = (() => {
        if (Array.isArray(stop.time_windows) && stop.time_windows.length > 0) {
          return stop.time_windows;
        }
        if (stop.time_window_start) {
          return [{ start: stop.time_window_start.slice(0, 5), end: (stop.time_window_end || '18:00').slice(0, 5) }];
        }
        return [{ start: '08:00', end: '18:00' }];
      })();

      // Look up dependency from stopDependencies if not directly in stop object
      let relPickup = stop.related_pickup_stop_id || null;
      let relDelivery = stop.related_delivery_stop_id || null;

      if (!relPickup && Array.isArray(stopDependencies)) {
        const depEdge = stopDependencies.find(d => d.successor_stop_id === stop.id);
        if (depEdge) relPickup = depEdge.predecessor_stop_id;
      }
      if (!relDelivery && Array.isArray(stopDependencies)) {
        const depEdge = stopDependencies.find(d => d.predecessor_stop_id === stop.id);
        if (depEdge) relDelivery = depEdge.successor_stop_id;
      }

      setFormData({
        id: stop.id,
        stop_number: stop.stop_number || `STOP-${stop.id}`,
        job_id: stop.job_id || null,
        client_id: stop.client_id || (clients[0]?.id || 1),
        type: stop.type || 'delivery',
        status: stop.status || 'pending',
        weight_kg: stop.weight_kg !== undefined ? stop.weight_kg : 100.0,
        volume_m3: stop.volume_m3 !== undefined ? stop.volume_m3 : 0.5,
        revenue: stop.revenue !== undefined ? stop.revenue : 0.0,
        service_time_mins: stop.service_time_mins !== undefined ? stop.service_time_mins : 10,
        sequence_order: stop.sequence_order !== undefined ? stop.sequence_order : 1,
        estimated_index: stop.estimated_index !== undefined ? stop.estimated_index : 1,
        real_index: stop.real_index !== undefined && stop.real_index !== null ? stop.real_index : '',
        estimated_arrival: stop.estimated_arrival || '08:00',
        actual_arrival: stop.actual_arrival ? String(stop.actual_arrival).slice(11, 16) : '',
        delivery_date: stop.delivery_date ? String(stop.delivery_date).slice(0, 10) : '2026-07-29',
        default_round_id: stop.default_round_id || '',
        instructions: stop.instructions || '',
        driver_notes: stop.driver_notes || '',
        time_windows: tws,
        related_pickup_stop_id: relPickup,
        related_delivery_stop_id: relDelivery
      });
      setIsConfirmingDelete(false);
      setIsDeleting(false);
    }
  }, [stop?.id, isOpen]);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      if (onDeleteStop) {
        await onDeleteStop(stop.id);
      } else {
        const res = await fetch(`/api/stops/${stop.id}`, { method: 'DELETE' });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || 'Erreur lors de la suppression de la livraison');
        }
        if (onReloadData) await onReloadData();
      }
      onClose();
    } catch (err) {
      console.error('Error deleting stop:', err);
      alert('Erreur lors de la suppression : ' + (err.message || err));
    } finally {
      setIsDeleting(false);
      setIsConfirmingDelete(false);
    }
  };

  const client = clients.find(c => c.id === parseInt(formData.client_id)) || {
    id: stop.client_id || 1,
    name: stop.client_name || 'Client Inconnu',
    address: stop.address || 'Adresse Inconnue',
    postal_code: stop.postal_code || '1000',
    city: stop.city || 'Lausanne',
    roadbook: stop.client_roadbook || ''
  };

  const assignedJob = jobs.find(j => j.id === parseInt(formData.job_id));

  const allStopsList = (() => {
    const combined = [...(stops || []), ...(unassignedStops || [])];
    const map = new Map();
    combined.forEach(s => { if (s && s.id) map.set(s.id, s); });
    return Array.from(map.values());
  })();

  const linkedPickupStop = formData.related_pickup_stop_id ? allStopsList.find(s => s.id === parseInt(formData.related_pickup_stop_id)) : null;
  const linkedDeliveryStop = formData.related_delivery_stop_id ? allStopsList.find(s => s.id === parseInt(formData.related_delivery_stop_id)) : null;

  const availableStopsToLink = allStopsList.filter(s => s.id !== formData.id && s.id !== formData.related_pickup_stop_id && s.id !== formData.related_delivery_stop_id);
  const predecessorEdges = (stopDependencies || []).filter(d => d.successor_stop_id === formData.id);
  const successorEdges = (stopDependencies || []).filter(d => d.predecessor_stop_id === formData.id);

  // Status background header color helper
  const getHeaderBgColor = () => {
    if (formData.status === 'delivered' || formData.status === 'completed' || formData.status === 'done') {
      return '#10b981'; // Green if done
    }
    if (formData.status === 'failed' || formData.status === 'failed_delivery' || formData.status === 'cancelled') {
      return '#ef4444'; // Red if failed
    }
    if (!formData.job_id) {
      return '#64748b'; // Grey if not planned
    }
    return '#2563eb'; // Blue if in a job
  };

  const handleAddTimeWindow = () => {
    setFormData(prev => ({
      ...prev,
      time_windows: [...prev.time_windows, { start: '14:00', end: '18:00' }]
    }));
  };

  const handleRemoveTimeWindow = (index) => {
    if (formData.time_windows.length <= 1) return;
    setFormData(prev => ({
      ...prev,
      time_windows: prev.time_windows.filter((_, i) => i !== index)
    }));
  };

  const handleTimeWindowChange = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.time_windows];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, time_windows: updated };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();

    let finalRelPickup = formData.related_pickup_stop_id ? parseInt(formData.related_pickup_stop_id) : null;
    let finalRelDelivery = formData.related_delivery_stop_id ? parseInt(formData.related_delivery_stop_id) : null;

    // Auto-link if user selected a stop in dropdown but didn't click "+ Lier"
    if (selectedLinkStopId) {
      const targetId = parseInt(selectedLinkStopId);
      if (formData.type === 'pickup') {
        finalRelDelivery = targetId;
      } else if (formData.type === 'delivery') {
        finalRelPickup = targetId;
      } else {
        finalRelDelivery = targetId;
      }
    }

    const cleanData = {
      ...formData,
      job_id: formData.job_id ? parseInt(formData.job_id) : null,
      client_id: formData.client_id ? parseInt(formData.client_id) : null,
      sequence_order: formData.sequence_order !== '' ? parseInt(formData.sequence_order) : 1,
      estimated_index: formData.estimated_index !== '' ? parseInt(formData.estimated_index) : 1,
      real_index: formData.real_index !== '' && formData.real_index !== null ? parseInt(formData.real_index) : null,
      weight_kg: formData.weight_kg !== '' ? parseFloat(formData.weight_kg) : 100.0,
      volume_m3: formData.volume_m3 !== '' ? parseFloat(formData.volume_m3) : 0.5,
      revenue: formData.revenue !== '' ? parseFloat(formData.revenue) : 0.0,
      service_time_mins: formData.service_time_mins !== '' ? parseInt(formData.service_time_mins) : 10,
      time_windows: formData.time_windows,
      related_pickup_stop_id: finalRelPickup,
      related_delivery_stop_id: finalRelDelivery
    };

    if (onSaveStop) {
      await onSaveStop(stop.id, cleanData);
    }
    onClose();
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 1100 }}>
      <div className="overlay-card" style={{ width: '800px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', padding: '0', overflow: 'hidden', borderRadius: '12px', background: '#0f172a', border: '1px solid #334155' }}>
        
        {/* HEADER BAR WITH STATUS COLORING */}
        <div style={{ background: getHeaderBgColor(), color: '#ffffff', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', transition: 'background-color 0.2s ease' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Package size={22} />
            <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '800' }}>
              {formData.type === 'pickup' ? 'Ramassage' : 'Livraison'}{' '}
              <span style={{ fontStyle: 'italic', fontWeight: '500' }}>{formData.stop_number}</span>
            </h2>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <select
              value={formData.status}
              onChange={e => setFormData({ ...formData, status: e.target.value })}
              style={{ background: 'rgba(0, 0, 0, 0.25)', color: '#fff', border: '1px solid rgba(255,255,255,0.4)', padding: '4px 10px', borderRadius: '6px', fontSize: '0.85rem', fontWeight: '700', outline: 'none' }}
            >
              <option value="pending" style={{ color: '#000' }}>À faire (En attente)</option>
              <option value="delivered" style={{ color: '#000' }}>Livrée / Effectuée</option>
              <option value="failed" style={{ color: '#000' }}>Problème / Échouée</option>
              <option value="cancelled" style={{ color: '#000' }}>Annulée</option>
            </select>

            <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: '4px' }}>
              <X size={22} />
            </button>
          </div>
        </div>

        {/* POPUP NAVIGATION TABS */}
        <div style={{ display: 'flex', background: '#1e293b', borderBottom: '1px solid #334155', padding: '0 16px' }}>
          <button
            type="button"
            className={`tab-item ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
            style={{
              padding: '10px 16px', fontSize: '0.84rem', fontWeight: '700', color: activeTab === 'general' ? '#38bdf8' : '#94a3b8',
              borderBottom: activeTab === 'general' ? '2px solid #38bdf8' : '2px solid transparent', background: 'none', borderTop: 'none', borderLeft: 'none', borderRight: 'none', cursor: 'pointer'
            }}
          >
            Général & Client
          </button>

          <button
            type="button"
            className={`tab-item ${activeTab === 'timewindows' ? 'active' : ''}`}
            onClick={() => setActiveTab('timewindows')}
            style={{
              padding: '10px 16px', fontSize: '0.84rem', fontWeight: '700', color: activeTab === 'timewindows' ? '#38bdf8' : '#94a3b8',
              borderBottom: activeTab === 'timewindows' ? '2px solid #38bdf8' : '2px solid transparent', background: 'none', borderTop: 'none', borderLeft: 'none', borderRight: 'none', cursor: 'pointer'
            }}
          >
            Créneaux Horaires ({formData.time_windows.length})
          </button>

          <button
            type="button"
            className={`tab-item ${activeTab === 'indexes' ? 'active' : ''}`}
            onClick={() => setActiveTab('indexes')}
            style={{
              padding: '10px 16px', fontSize: '0.84rem', fontWeight: '700', color: activeTab === 'indexes' ? '#38bdf8' : '#94a3b8',
              borderBottom: activeTab === 'indexes' ? '2px solid #38bdf8' : '2px solid transparent', background: 'none', borderTop: 'none', borderLeft: 'none', borderRight: 'none', cursor: 'pointer'
            }}
          >
            Index & Ordre
          </button>

          <button
            type="button"
            className={`tab-item ${activeTab === 'payload' ? 'active' : ''}`}
            onClick={() => setActiveTab('payload')}
            style={{
              padding: '10px 16px', fontSize: '0.84rem', fontWeight: '700', color: activeTab === 'payload' ? '#38bdf8' : '#94a3b8',
              borderBottom: activeTab === 'payload' ? '2px solid #38bdf8' : '2px solid transparent', background: 'none', borderTop: 'none', borderLeft: 'none', borderRight: 'none', cursor: 'pointer'
            }}
          >
            Charge & Volume
          </button>

          <button
            type="button"
            className={`tab-item ${activeTab === 'notes' ? 'active' : ''}`}
            onClick={() => setActiveTab('notes')}
            style={{
              padding: '10px 16px', fontSize: '0.84rem', fontWeight: '700', color: activeTab === 'notes' ? '#38bdf8' : '#94a3b8',
              borderBottom: activeTab === 'notes' ? '2px solid #38bdf8' : '2px solid transparent', background: 'none', borderTop: 'none', borderLeft: 'none', borderRight: 'none', cursor: 'pointer'
            }}
          >
            Notes & Instructions
          </button>
        </div>

        {/* MODAL FORM CONTENT */}
        <form onSubmit={handleSave} style={{ flex: 1, overflowY: 'auto', padding: '20px', background: '#0f172a', color: '#f8fafc', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          
          {/* TAB 1: GÉNÉRAL & CLIENT */}
          {activeTab === 'general' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              {/* Left Column Fields */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '0.78rem', fontWeight: '700', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>
                    Code / Numéro du Stop
                  </label>
                  <input
                    type="text"
                    className="table-input"
                    style={{ width: '100%', padding: '6px 10px', fontSize: '0.85rem', background: '#1e293b', color: '#fff', border: '1px solid #334155' }}
                    value={formData.stop_number}
                    onChange={e => setFormData({ ...formData, stop_number: e.target.value })}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '0.78rem', fontWeight: '700', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>
                    Type d'opération
                  </label>
                  <select
                    className="table-input"
                    style={{
                      width: '100%',
                      padding: '6px 10px',
                      fontSize: '0.85rem',
                      background: '#1e293b',
                      color: formData.type === 'pickup' ? '#f97316' : '#38bdf8',
                      border: `1px solid ${formData.type === 'pickup' ? '#f97316' : '#334155'}`,
                      fontWeight: '800'
                    }}
                    value={formData.type || 'delivery'}
                    onChange={e => setFormData(prev => ({ ...prev, type: e.target.value }))}
                  >
                    <option value="delivery" style={{ color: '#38bdf8', background: '#0f172a', fontWeight: '700' }}>📦↘ LIVRAISON</option>
                    <option value="pickup" style={{ color: '#f97316', background: '#0f172a', fontWeight: '700' }}>📦↗ RAMASSAGE</option>
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: '0.78rem', fontWeight: '700', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>
                    Date de livraison
                  </label>
                  <input
                    type="date"
                    className="table-input"
                    style={{ width: '100%', padding: '6px 10px', fontSize: '0.85rem', background: '#1e293b', color: '#fff', border: '1px solid #334155' }}
                    value={formData.delivery_date}
                    onChange={e => setFormData({ ...formData, delivery_date: e.target.value })}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '0.78rem', fontWeight: '700', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>
                    Mission
                  </label>
                  <div style={{ padding: '6px 10px', fontSize: '0.85rem', background: '#1e293b', color: '#ffffff', border: '1px solid #334155', borderRadius: '4px', fontWeight: '600' }}>
                    {assignedJob ? (assignedJob.display_name || `Mission #${assignedJob.id}`) : '--'}
                  </div>
                </div>

                <div>
                  <label style={{ fontSize: '0.78rem', fontWeight: '700', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>
                    Tournée par Défaut (Modèle)
                  </label>
                  <select
                    className="table-input"
                    style={{ width: '100%', padding: '6px 10px', fontSize: '0.85rem', background: '#1e293b', color: '#ffffff', border: '1px solid #334155', fontWeight: '700' }}
                    value={formData.default_round_id || ''}
                    onChange={e => setFormData({ ...formData, default_round_id: e.target.value ? parseInt(e.target.value) : null })}
                  >
                    <option value="">--</option>
                    {rounds.map(r => (
                      <option key={r.id} value={r.id}>{r.name} ({r.code})</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Right Column: Encadré Client & Encadré Pickups/Deliveries Liés */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* Client Card */}
                <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', padding: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ fontSize: '0.78rem', color: '#38bdf8', fontWeight: '700' }}>Client #{client.id}</span>
                    <select
                      style={{ background: '#0f172a', color: '#fff', border: '1px solid #334155', fontSize: '0.75rem', padding: '2px 6px', borderRadius: '4px' }}
                      value={formData.client_id}
                      onChange={e => setFormData({ ...formData, client_id: parseInt(e.target.value) })}
                    >
                      {clients.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>

                  <h3 style={{ fontSize: '1.05rem', fontWeight: '800', color: '#ffffff', margin: '0 0 8px 0' }}>{client.name}</h3>

                  <div style={{ fontSize: '0.82rem', color: '#94a3b8', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div>📍 {client.address}</div>
                    <div>{client.postal_code} {client.city}</div>
                    {client.roadbook && (
                      <div style={{ background: 'rgba(56, 189, 248, 0.15)', border: '1px solid rgba(56, 189, 248, 0.3)', color: '#38bdf8', padding: '6px 10px', borderRadius: '4px', fontSize: '0.78rem', marginTop: '6px' }}>
                        ✔️ <b>Roadbook:</b> {client.roadbook}
                      </div>
                    )}
                  </div>
                </div>

                {/* Encadré Relations de Précédence (Avant / Après) */}
                <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.8rem', color: '#38bdf8', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      🔗 Dépendances
                    </span>
                  </div>

                  {/* STOPS PRÉCÉDENTS */}
                  <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '6px', padding: '8px 10px' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: '500', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>
                      Stops précédents
                    </span>
                    {predecessorEdges.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {predecessorEdges.map(dep => {
                          const pStop = allStopsList.find(s => s.id === dep.predecessor_stop_id);
                          const cName = pStop?.client_name || clients.find(c => c.id === pStop?.client_id)?.name || pStop?.city || 'Client';
                          return (
                            <div key={dep.id || `${dep.predecessor_stop_id}-${dep.successor_stop_id}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#1e293b', padding: '4px 8px', borderRadius: '4px', fontSize: '0.75rem' }}>
                              <span style={{ color: '#38bdf8', fontWeight: '700' }}>
                                {pStop?.stop_number || `STOP-${dep.predecessor_stop_id}`} <span style={{ color: '#94a3b8', fontWeight: '400' }}>({cName})</span>
                              </span>
                              <button
                                type="button"
                                onClick={async () => {
                                  await fetch('/api/stop-dependencies', {
                                    method: 'DELETE',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ predecessor_stop_id: dep.predecessor_stop_id, successor_stop_id: formData.id })
                                  });
                                  if (onReloadData) await onReloadData();
                                }}
                                style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0 2px' }}
                                title="Supprimer la dépendance"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <span style={{ fontSize: '0.72rem', color: '#64748b', fontStyle: 'italic' }}>Aucun stop précédent</span>
                    )}

                    {/* Instant Dropdown to add a Predecessor */}
                    <div style={{ marginTop: '6px' }}>
                      <select
                        value=""
                        onChange={async (e) => {
                          const val = e.target.value;
                          if (!val) return;
                          await fetch('/api/stop-dependencies', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ predecessor_stop_id: parseInt(val), successor_stop_id: formData.id, dependency_type: 'precedence' })
                          });
                          if (onReloadData) await onReloadData();
                        }}
                        style={{ width: '100%', background: '#1e293b', color: '#fff', border: '1px solid #334155', borderRadius: '4px', padding: '5px 8px', fontSize: '0.75rem', cursor: 'pointer' }}
                      >
                        <option value="">+ Ajouter un stop AVANT ce stop...</option>
                        {allStopsList.filter(s => s.id !== formData.id && !predecessorEdges.some(d => d.predecessor_stop_id === s.id)).map(s => {
                          const cName = s.client_name || clients.find(c => c.id === s.client_id)?.name || s.city || 'Client';
                          return (
                            <option key={s.id} value={s.id}>
                              {s.stop_number || `STOP-${s.id}`} - {cName}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>

                  {/* STOPS SUIVANTS */}
                  <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '6px', padding: '8px 10px' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: '500', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>
                      Stops suivants
                    </span>
                    {successorEdges.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {successorEdges.map(dep => {
                          const sStop = allStopsList.find(s => s.id === dep.successor_stop_id);
                          const cName = sStop?.client_name || clients.find(c => c.id === sStop?.client_id)?.name || sStop?.city || 'Client';
                          return (
                            <div key={dep.id || `${dep.predecessor_stop_id}-${dep.successor_stop_id}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#1e293b', padding: '4px 8px', borderRadius: '4px', fontSize: '0.75rem' }}>
                              <span style={{ color: '#22c55e', fontWeight: '700' }}>
                                {sStop?.stop_number || `STOP-${dep.successor_stop_id}`} <span style={{ color: '#94a3b8', fontWeight: '400' }}>({cName})</span>
                              </span>
                              <button
                                type="button"
                                onClick={async () => {
                                  await fetch('/api/stop-dependencies', {
                                    method: 'DELETE',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ predecessor_stop_id: formData.id, successor_stop_id: dep.successor_stop_id })
                                  });
                                  if (onReloadData) await onReloadData();
                                }}
                                style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0 2px' }}
                                title="Supprimer la dépendance"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <span style={{ fontSize: '0.72rem', color: '#64748b', fontStyle: 'italic' }}>Aucun stop suivant</span>
                    )}

                    {/* Instant Dropdown to add a Successor */}
                    <div style={{ marginTop: '6px' }}>
                      <select
                        value=""
                        onChange={async (e) => {
                          const val = e.target.value;
                          if (!val) return;
                          await fetch('/api/stop-dependencies', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ predecessor_stop_id: formData.id, successor_stop_id: parseInt(val), dependency_type: 'precedence' })
                          });
                          if (onReloadData) await onReloadData();
                        }}
                        style={{ width: '100%', background: '#1e293b', color: '#fff', border: '1px solid #334155', borderRadius: '4px', padding: '5px 8px', fontSize: '0.75rem', cursor: 'pointer' }}
                      >
                        <option value="">+ Ajouter un stop APRÈS ce stop...</option>
                        {allStopsList.filter(s => s.id !== formData.id && !successorEdges.some(d => d.successor_stop_id === s.id)).map(s => {
                          const cName = s.client_name || clients.find(c => c.id === s.client_id)?.name || s.city || 'Client';
                          return (
                            <option key={s.id} value={s.id}>
                              {s.stop_number || `STOP-${s.id}`} - {cName}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: CRÉNEAUX HORAIRES MULTIPLES */}
          {activeTab === 'timewindows' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#38bdf8' }}>Créneaux Horaires de Livraison</h4>
                  <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                    Définissez un ou plusieurs créneaux horaires autorisés pour ce stop
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleAddTimeWindow}
                  className="btn-action"
                  style={{ background: '#38bdf8', color: '#0f172a', border: 'none', fontWeight: '700', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  <Plus size={14} />
                  <span>Ajouter un créneau</span>
                </button>
              </div>

              <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', padding: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {formData.time_windows.map((tw, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#0f172a', padding: '10px 14px', borderRadius: '6px', border: '1px solid #334155' }}>
                    <span style={{ fontSize: '0.8rem', fontWeight: '700', color: '#38bdf8', width: '80px' }}>
                      Créneau #{idx + 1}
                    </span>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>De:</span>
                      <input
                        type="time"
                        className="table-input"
                        style={{ width: '90px', padding: '4px 6px', background: '#1e293b', color: '#fff', border: '1px solid #334155', textAlign: 'center' }}
                        value={tw.start ? tw.start.slice(0, 5) : '08:00'}
                        onChange={e => handleTimeWindowChange(idx, 'start', e.target.value)}
                      />
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>À:</span>
                      <input
                        type="time"
                        className="table-input"
                        style={{ width: '90px', padding: '4px 6px', background: '#1e293b', color: '#fff', border: '1px solid #334155', textAlign: 'center' }}
                        value={tw.end ? tw.end.slice(0, 5) : '18:00'}
                        onChange={e => handleTimeWindowChange(idx, 'end', e.target.value)}
                      />
                    </div>

                    {formData.time_windows.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveTimeWindow(idx)}
                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', marginLeft: 'auto', padding: '4px' }}
                        title="Supprimer ce créneau"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginTop: '6px' }}>
                <div>
                  <label style={{ fontSize: '0.78rem', fontWeight: '700', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>
                    Temps de service au stop (minutes)
                  </label>
                  <input
                    type="number"
                    className="table-input"
                    style={{ width: '100%', padding: '6px 10px', fontSize: '0.85rem', background: '#1e293b', color: '#fff', border: '1px solid #334155' }}
                    value={formData.service_time_mins}
                    onChange={e => setFormData({ ...formData, service_time_mins: e.target.value })}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '0.78rem', fontWeight: '700', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>
                    Heure d'arrivée estimée
                  </label>
                  <input
                    type="time"
                    className="table-input"
                    style={{ width: '100%', padding: '6px 10px', fontSize: '0.85rem', background: '#1e293b', color: '#22c55e', border: '1px solid #334155', fontWeight: '700' }}
                    value={formData.estimated_arrival}
                    onChange={e => setFormData({ ...formData, estimated_arrival: e.target.value })}
                  />
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: INDEX & ORDRE */}
          {activeTab === 'indexes' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px' }}>
              <div style={{ background: '#1e293b', border: '1px solid #334155', padding: '14px', borderRadius: '8px' }}>
                <label style={{ fontSize: '0.78rem', fontWeight: '700', color: '#94a3b8', display: 'block', marginBottom: '6px' }}>
                  Sequence Order (Ordre Général)
                </label>
                <input
                  type="number"
                  className="table-input"
                  style={{ width: '100%', padding: '8px 10px', fontSize: '0.9rem', background: '#0f172a', color: '#fff', border: '1px solid #334155', fontWeight: '700' }}
                  value={formData.sequence_order}
                  onChange={e => setFormData({ ...formData, sequence_order: e.target.value })}
                />
                <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '6px' }}>Ordre brut en base de données</div>
              </div>

              <div style={{ background: '#1e293b', border: '1px solid #334155', padding: '14px', borderRadius: '8px' }}>
                <label style={{ fontSize: '0.78rem', fontWeight: '700', color: '#38bdf8', display: 'block', marginBottom: '6px' }}>
                  Index Estimé (Planning)
                </label>
                <input
                  type="number"
                  className="table-input"
                  style={{ width: '100%', padding: '8px 10px', fontSize: '0.9rem', background: '#0f172a', color: '#38bdf8', border: '1px solid #334155', fontWeight: '700' }}
                  value={formData.estimated_index}
                  onChange={e => setFormData({ ...formData, estimated_index: e.target.value })}
                />
                <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '6px' }}>Utilisé tant que la mission est à planifier</div>
              </div>

              <div style={{ background: '#1e293b', border: '1px solid #334155', padding: '14px', borderRadius: '8px' }}>
                <label style={{ fontSize: '0.78rem', fontWeight: '700', color: '#22c55e', display: 'block', marginBottom: '6px' }}>
                  Index Réel (Terrain)
                </label>
                <input
                  type="number"
                  placeholder="Vide (null)"
                  className="table-input"
                  style={{ width: '100%', padding: '8px 10px', fontSize: '0.9rem', background: '#0f172a', color: '#22c55e', border: '1px solid #334155', fontWeight: '700' }}
                  value={formData.real_index}
                  onChange={e => setFormData({ ...formData, real_index: e.target.value })}
                />
                <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '6px' }}>Enregistré lorsque le livreur effectue le stop</div>
              </div>
            </div>
          )}

          {/* TAB 4: CHARGE & VOLUME */}
          {activeTab === 'payload' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px' }}>
              <div style={{ background: '#1e293b', border: '1px solid #334155', padding: '14px', borderRadius: '8px' }}>
                <label style={{ fontSize: '0.78rem', fontWeight: '700', color: '#94a3b8', display: 'block', marginBottom: '6px' }}>
                  Poids (kg)
                </label>
                <input
                  type="number"
                  step="0.1"
                  className="table-input"
                  style={{ width: '100%', padding: '8px 10px', fontSize: '0.9rem', background: '#0f172a', color: '#22c55e', border: '1px solid #334155', fontWeight: '700' }}
                  value={formData.weight_kg}
                  onChange={e => setFormData({ ...formData, weight_kg: e.target.value })}
                />
              </div>

              <div style={{ background: '#1e293b', border: '1px solid #334155', padding: '14px', borderRadius: '8px' }}>
                <label style={{ fontSize: '0.78rem', fontWeight: '700', color: '#94a3b8', display: 'block', marginBottom: '6px' }}>
                  Volume (m³)
                </label>
                <input
                  type="number"
                  step="0.01"
                  className="table-input"
                  style={{ width: '100%', padding: '8px 10px', fontSize: '0.9rem', background: '#0f172a', color: '#f59e0b', border: '1px solid #334155', fontWeight: '700' }}
                  value={formData.volume_m3}
                  onChange={e => setFormData({ ...formData, volume_m3: e.target.value })}
                />
              </div>

              <div style={{ background: '#1e293b', border: '1px solid #334155', padding: '14px', borderRadius: '8px' }}>
                <label style={{ fontSize: '0.78rem', fontWeight: '700', color: '#94a3b8', display: 'block', marginBottom: '6px' }}>
                  Chiffre d'Affaires Stop (CHF)
                </label>
                <input
                  type="number"
                  step="0.01"
                  className="table-input"
                  style={{ width: '100%', padding: '8px 10px', fontSize: '0.9rem', background: '#0f172a', color: '#38bdf8', border: '1px solid #334155', fontWeight: '700' }}
                  value={formData.revenue}
                  onChange={e => setFormData({ ...formData, revenue: e.target.value })}
                />
              </div>
            </div>
          )}

          {/* TAB 5: NOTES & INSTRUCTIONS */}
          {activeTab === 'notes' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: '700', color: '#38bdf8', display: 'block', marginBottom: '6px' }}>
                  📝 Instructions de livraison (Client / Exploitation)
                </label>
                <textarea
                  className="table-input"
                  style={{ width: '100%', minHeight: '90px', padding: '8px 12px', fontSize: '0.85rem', background: '#1e293b', color: '#fff', border: '1px solid #334155' }}
                  value={formData.instructions}
                  onChange={e => setFormData({ ...formData, instructions: e.target.value })}
                  placeholder="Instructions spécifiques pour la livraison..."
                />
              </div>

              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: '700', color: '#f59e0b', display: 'block', marginBottom: '6px' }}>
                  📌 Notes du Chauffeur (Saisie terrain)
                </label>
                <textarea
                  className="table-input"
                  style={{ width: '100%', minHeight: '90px', padding: '8px 12px', fontSize: '0.85rem', background: '#1e293b', color: '#fff', border: '1px solid #334155' }}
                  value={formData.driver_notes}
                  onChange={e => setFormData({ ...formData, driver_notes: e.target.value })}
                  placeholder="Notes et remarques saisies par le chauffeur..."
                />
              </div>
            </div>
          )}

          {/* FOOTER ACTION BUTTONS */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginTop: '10px', paddingTop: '14px', borderTop: '1px solid #334155' }}>
            {/* Red Delete Button with Double Confirmation */}
            {!isConfirmingDelete ? (
              <button
                type="button"
                onClick={() => setIsConfirmingDelete(true)}
                style={{
                  padding: '8px 16px',
                  background: 'rgba(239, 68, 68, 0.12)',
                  color: '#ef4444',
                  border: '1px solid rgba(239, 68, 68, 0.4)',
                  borderRadius: '6px',
                  fontSize: '0.82rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
                  e.currentTarget.style.borderColor = '#ef4444';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.12)';
                  e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.4)';
                }}
                title="Supprimer cette livraison"
              >
                <Trash2 size={15} />
                <span>Supprimer la livraison</span>
              </button>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(239, 68, 68, 0.15)', border: '1px solid #ef4444', padding: '4px 8px', borderRadius: '8px' }}>
                <span style={{ fontSize: '0.78rem', color: '#fca5a5', fontWeight: '700' }}>
                  Supprimer définitivement ?
                </span>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isDeleting}
                  style={{
                    background: '#dc2626',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '5px 12px',
                    fontSize: '0.78rem',
                    fontWeight: '800',
                    cursor: isDeleting ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    boxShadow: '0 2px 8px rgba(220, 38, 38, 0.4)'
                  }}
                >
                  <Trash2 size={13} />
                  <span>{isDeleting ? 'Suppression...' : 'Oui, supprimer'}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setIsConfirmingDelete(false)}
                  disabled={isDeleting}
                  style={{
                    background: 'transparent',
                    color: '#94a3b8',
                    border: '1px solid #475569',
                    borderRadius: '6px',
                    padding: '5px 10px',
                    fontSize: '0.78rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Non
                </button>
              </div>
            )}

            <button type="submit" className="btn-action" style={{ background: '#10b981', color: '#fff', border: 'none', fontWeight: '700', padding: '8px 20px' }}>
              Enregistrer dans la base de données
            </button>
          </div>

        </form>

      </div>
    </div>
  );
}
